/** Global state governing the Confimartion Modal */

import { atom, useAtom } from 'jotai'
import { useCallback } from 'react'

type ConfirmationModalColor = 'primary' | 'red' | 'orange'
type ConfirmationModalState = {
  isOpen: boolean
  title: string
  description: string
  confirmButtonCaption: string
  onConfirm: () => void
  color: ConfirmationModalColor
}
const confirmationModalDefaultValues: ConfirmationModalState = {
  isOpen: false,
  title: '',
  description: '',
  confirmButtonCaption: '',
  onConfirm: () => {},
  color: 'primary',
}

export type ConfirmationModalOnConfirmCallback = () => void

const isConfirmationModalOpen = atom(confirmationModalDefaultValues.isOpen)
const confirmationModalTitle = atom(confirmationModalDefaultValues.title)
const confirmationModalColor = atom<ConfirmationModalColor>(
  confirmationModalDefaultValues.color
)
const confirmationModalDescription = atom(
  confirmationModalDefaultValues.description
)
const confirmationModalConfirmButtonCaption = atom(
  confirmationModalDefaultValues.confirmButtonCaption
)
const confirmationModalOnConfirm = atom<{
  fn: ConfirmationModalOnConfirmCallback
}>({
  fn: confirmationModalDefaultValues.onConfirm,
})

export function useConfirmationModal() {
  const [isOpen, setIsOpen] = useAtom(isConfirmationModalOpen)
  const [title, setTitle] = useAtom(confirmationModalTitle)
  const [description, setDescription] = useAtom(confirmationModalDescription)
  const [confirmButtonCaption, setConfirmButtonCaption] = useAtom(
    confirmationModalConfirmButtonCaption
  )
  const [color, setColor] = useAtom(confirmationModalColor)
  const [onConfirm, setOnConfirm] = useAtom(confirmationModalOnConfirm)

  const show = useCallback(
    ({
      title,
      description,
      confirmButtonCaption,
      onConfirm,
      color = 'primary',
    }: {
      title: string
      description: string
      confirmButtonCaption: string
      onConfirm: ConfirmationModalOnConfirmCallback
      color?: ConfirmationModalColor
    }) => {
      setTitle(title)
      setDescription(description)
      setConfirmButtonCaption(confirmButtonCaption)
      setOnConfirm({ fn: onConfirm })
      setColor(color)
      setIsOpen(true)
    },
    [
      setTitle,
      setDescription,
      setConfirmButtonCaption,
      setOnConfirm,
      setColor,
      setIsOpen,
    ]
  )

  const close = useCallback(() => {
    setIsOpen(false)
    // Timeout to play nicely with Dialog animation
    setTimeout(() => {
      setTitle(confirmationModalDefaultValues.title)
      setDescription(confirmationModalDefaultValues.description)
      setConfirmButtonCaption(
        confirmationModalDefaultValues.confirmButtonCaption
      )
      setColor(confirmationModalDefaultValues.color)
      setOnConfirm({
        fn: confirmationModalDefaultValues.onConfirm,
      })
    }, 500)
  }, [
    setIsOpen,
    setTitle,
    setDescription,
    setConfirmButtonCaption,
    setColor,
    setOnConfirm,
  ])

  return {
    isOpen,
    title,
    description,
    confirmButtonCaption,
    color,
    onConfirm: onConfirm.fn,
    show,
    close,
  }
}
